import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import Image from 'components/Image';
import Article from './Article';
import styles from './EmailCapture.module.scss';

const EmailForm = dynamic(() => import('./EmailForm'), {
  ssr: false,
});

const NAVY = 'navy';
const GOLD = 'gold';
const CREAM = 'cream';
const WHITE = 'white';

export function EmailCapture({ cmsData, onTiledHomePage = false }) {
  const {
    advisory,
    anchor_id: anchorId,
    background_color: backgroundColor,
    background_image: background,
    cta_theme: ctaTheme,
    email_label: emailLabel,
    email_placeholder: emailPlaceholder,
    header,
    input_color: inputColor,
    klaviyo_custom_property: klaviyoCustomProperty,
    success_header: successHeader,
    success_advisory: successAdvisory,
    translucent_background_rectangle: translucentBackgroundRectangle = 'none',
    uid,
  } = cmsData ?? {};
  const showNewsletter = useSelector((state) => state.featureFlags.newsletter);
  const userInfo = useSelector((state) => state.user.userInfo);

  const [successSignUp, setSuccessSignUp] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(true);

  const borderTextClass = classNames({
    'text-primary border-primary': inputColor === NAVY,
    'text-cream border-cream': inputColor === CREAM,
    'text-white border-white': inputColor === WHITE,
  });

  useEffect(() => {
    const { klaviyoProperties, isEnrolledMarketing } = userInfo ?? {};
    if (showNewsletter) {
      setShowEmailCapture(
        !isEnrolledMarketing ||
          successSignUp ||
          (klaviyoCustomProperty && !klaviyoProperties?.[klaviyoCustomProperty])
      );
    } else {
      setShowEmailCapture(false);
    }
  }, [
    klaviyoCustomProperty,
    showNewsletter,
    successSignUp,
    userInfo.isEnrolledMarketing,
    userInfo.klaviyoProperties,
  ]);

  return showEmailCapture ? (
    <aside className="w-100 h-100 position-relative" id={anchorId}>
      <div className="position-absolute w-100 h-100 top-0 bottom-0 start-0 end-0">
        <Image
          fill
          src={background?.url}
          objectFit="cover"
          alt={advisory}
          sizes="100vw"
        />
      </div>
      <div
        className={classNames(
          `position-relative d-flex flex-row flex-wrap align-items-center justify-content-center`,
          styles.section,
          {
            'bg-primary': backgroundColor === NAVY && !background?.url,
            'bg-secondary': backgroundColor === GOLD && !background?.url,
            'bg-cream': backgroundColor === CREAM && !background?.url,
            'bg-white': backgroundColor === WHITE && !background?.url,
            'bg-transparent': background?.url,
            'px-sm-20 py-sm-15': !onTiledHomePage,
            'px-lg-20 h-100': onTiledHomePage,
          },
          borderTextClass
        )}
      >
        <div
          className={classNames('rounded', {
            [styles.navyRectangle]: translucentBackgroundRectangle === NAVY,
            [styles.goldRectangle]: translucentBackgroundRectangle === GOLD,
            [styles.creamRectangle]: translucentBackgroundRectangle === CREAM,
            [styles.whiteRectangle]: translucentBackgroundRectangle === WHITE,
            'px-10 py-5': !onTiledHomePage,
            'p-5 px-md-10 py-md-5': onTiledHomePage,
            [styles.tiledHomePage]: onTiledHomePage,
          })}
        >
          {successSignUp ? (
            <Article message={successAdvisory} title={successHeader} />
          ) : (
            <div className="m-auto">
              <Article
                message={advisory}
                title={header}
                useTiledHomePageStyle={onTiledHomePage}
              />
              <div className="w-md-75 m-auto">
                <EmailForm
                  className={borderTextClass}
                  controlId={uid}
                  ctaVariant={ctaTheme}
                  emailAddressLabel={emailLabel}
                  emailAddressPlaceholder={emailPlaceholder}
                  formGroupClassName={[
                    borderTextClass,
                    'font-sofia text-uppercase',
                  ]}
                  klaviyoCustomProperty={klaviyoCustomProperty}
                  labelClassName={borderTextClass}
                  setSuccessSignUp={setSuccessSignUp}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  ) : null;
}

export default EmailCapture;
